import { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography, CircularProgress } from "@mui/material";
import DataTable from "shared/components/Tables/DataTable";
import { VIP_USAGE_COLUMNS, vipHistoryColumns, usageCell } from "modules/came/model/vipUsageModel";

function VipValueCell({ value, column, row }) {
  const delayed =
    typeof value === "number" && value > 15 && ["entryMinutes", "exitMinutes"].includes(column.id);
  return (
    <Box
      component="span"
      sx={{ color: delayed ? "error.main" : "text.primary", fontSize: "0.75rem" }}
    >
      {usageCell(row.original, column.id)}
    </Box>
  );
}
VipValueCell.propTypes = {
  value: PropTypes.any,
  row: PropTypes.shape({ original: PropTypes.object.isRequired }).isRequired,
  column: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
};

export default function UsoVipTable({ report, view, loading }) {
  const history = view === "history";
  const table = useMemo(
    () => ({
      columns: (history ? vipHistoryColumns(report) : VIP_USAGE_COLUMNS).map(([key, label]) => ({
        Header: label,
        accessor: key,
        Cell: VipValueCell,
        sortType: ["entryMinutes", "exitMinutes", "totalMinutes", "vipMinutes"].includes(key)
          ? "basic"
          : "alphanumeric",
        align: "left",
      })),
      rows: (history ? report?.history : report?.rows) || [],
    }),
    [history, report]
  );
  if (loading)
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <CircularProgress size={26} />
        <Typography variant="body2">Consultando Uso de VIP…</Typography>
      </Box>
    );
  if (!table.rows.length)
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
        {report
          ? "No hay registros para los filtros seleccionados."
          : "Aplica los filtros para consultar Uso de VIP."}
      </Typography>
    );
  return (
    <Box
      role="region"
      aria-label={history ? "Historial de movimientos VIP" : "Tabla de Uso de VIP"}
      sx={{
        pt: 2,
        "& .MuiTableContainer-root": {
          maxHeight: "65vh",
          overflow: "auto",
          scrollbarWidth: "thin",
        },
        "& table": { minWidth: history ? 1500 : 2100 },
        "& thead": { display: "table-header-group", p: 0 },
        "& th, & td": { whiteSpace: "nowrap", fontSize: "0.75rem" },
        "& thead th": { position: "sticky", top: 0, zIndex: 2, bgcolor: "#16364b", color: "#fff" },
        "& thead th > div": { color: "#fff", opacity: 1, pr: 3 },
        "& tbody tr:nth-of-type(even)": { bgcolor: "action.hover" },
        "& tbody td:first-of-type": {
          position: "sticky",
          left: 0,
          bgcolor: "background.paper",
          zIndex: 1,
        },
        "& thead th:first-of-type": { left: 0, zIndex: 3 },
      }}
    >
      <DataTable
        key={view}
        table={table}
        entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
        canSearch={false}
        isSorted
        showTotalEntries
        autoResetPage
      />
    </Box>
  );
}
UsoVipTable.propTypes = {
  report: PropTypes.object,
  view: PropTypes.oneOf(["visits", "history"]).isRequired,
  loading: PropTypes.bool,
};
